import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { waitlistApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  List,
  Search,
  FileSpreadsheet,
  Trash2,
  ArrowLeft,
  Phone,
  MapPin,
  Calendar,
  Wifi,
  LogOut,
  Map,
  History,
  Users,
  Settings,
  CheckCircle2,
} from "lucide-react";

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function WaitlistPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [onlyViable, setOnlyViable] = useState(false);

  const isNOC = user?.role === "noc";
  const viableCount = entries.filter((e) => e.status === "viavel_agora").length;

  // Load waitlist
  useEffect(() => {
    const loadWaitlist = async () => {
      setLoading(true);
      try {
        const response = await waitlistApi.getAll();
        setEntries(response.data);
        setFilteredEntries(response.data);
      } catch (error) {
        toast.error("Erro ao carregar fila de espera");
      } finally {
        setLoading(false);
      }
    };
    loadWaitlist();
  }, []);

  // Filter entries
  useEffect(() => {
    const base = onlyViable ? entries.filter((e) => e.status === "viavel_agora") : entries;
    if (!searchTerm) {
      setFilteredEntries(base);
      return;
    }

    const filtered = base.filter(
      (entry) =>
        entry.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.phone.includes(searchTerm) ||
        entry.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEntries(filtered);
  }, [searchTerm, entries, onlyViable]);

  // Export to Excel
  const handleExport = async () => {
    try {
      const response = await waitlistApi.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fila_espera_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportação concluída!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  // Delete entry
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await waitlistApi.delete(deleteId);
      setEntries(entries.filter((e) => e.id !== deleteId));
      toast.success("Entrada removida");
    } catch (error) {
      toast.error("Erro ao remover entrada");
    } finally {
      setDeleteId(null);
    }
  };

  // Sidebar for NOC
  const Sidebar = () => (
    <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
            <Wifi className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
            <p className="text-xs text-slate-400">NOC Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? "bg-[#FF4500] text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );

  // Sales header
  const SalesHeader = () => (
    <header className="bg-slate-900 border-b border-slate-800 p-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">TECPLUS</h1>
              <p className="text-xs text-slate-400">Fila de Espera</p>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={logout}
          className="text-slate-400 hover:text-white"
        >
          <LogOut className="w-5 h-5" />
        </Button>
      </div>
    </header>
  );

  const MainContent = () => (
    <main className={`flex-1 p-8 overflow-auto ${!isNOC ? "bg-slate-950" : ""}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Fila de Espera</h2>
            <p className="text-slate-400">
              {filteredEntries.length} registro{filteredEntries.length !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOnlyViable(!onlyViable)}
              className={onlyViable ? "bg-green-600 border-green-600 text-white hover:bg-green-700" : "border-slate-700 text-slate-300 hover:bg-slate-800"}
              data-testid="filter-viable-button"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Viáveis agora ({viableCount})
            </Button>
            {isNOC && (
              <Button
                onClick={handleExport}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar por nome, telefone ou endereço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="search-input"
          />
        </div>

        {/* Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="text-center py-12">
                <List className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Nenhum registro encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Cliente</TableHead>
                      <TableHead className="text-slate-400">Telefone</TableHead>
                      <TableHead className="text-slate-400">Endereço</TableHead>
                      <TableHead className="text-slate-400">Distância</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Data</TableHead>
                      {isNOC && <TableHead className="text-slate-400 w-[80px]">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        className="border-slate-800 hover:bg-slate-800/50"
                        data-testid={`waitlist-row-${entry.id}`}
                      >
                        <TableCell className="text-white font-medium">
                          {entry.client_name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-300">
                            <Phone className="w-4 h-4 text-slate-500" />
                            <span className="font-mono">{entry.phone}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2 max-w-[300px]">
                            <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-300 text-sm line-clamp-2">
                              {entry.address}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`font-mono ${entry.status === "viavel_agora" ? "text-slate-500 line-through" : "text-red-400"}`}>
                            {entry.distance_meters?.toFixed(0)}m
                          </span>
                        </TableCell>
                        <TableCell data-testid={`waitlist-status-${entry.id}`}>
                          {entry.status === "viavel_agora" ? (
                            <div>
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400">
                                Viável agora
                              </span>
                              <p className="text-xs text-slate-500 mt-1 font-mono">
                                {entry.now_viable_cto} · {entry.now_viable_distance?.toFixed(0)}m
                              </p>
                            </div>
                          ) : (
                            <span className="inline-flex px-2 py-1 rounded-full text-xs font-bold bg-slate-700 text-slate-300">
                              Aguardando
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {new Date(entry.created_at).toLocaleDateString("pt-BR")}
                          </div>
                        </TableCell>
                        {isNOC && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(entry.id)}
                              className="text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                              data-testid={`delete-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="waitlist-page">
      {isNOC ? <Sidebar /> : null}
      <div className="flex-1 flex flex-col">
        {!isNOC && <SalesHeader />}
        <MainContent />
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tem certeza que deseja remover este registro da fila de espera?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
